/**
 * Knowledge base archive: category filters, live hero search (REST), panel toggling.
 */
document.addEventListener( 'DOMContentLoaded', ( ) => {
  const root = document.querySelector( '.b3rg-knowledge-base-archive' );
  if ( !root ) {
    return;
  }

  const buttons = root.querySelectorAll( '.b3rg-kb-archive__filter-btn' );
  const browseWrap = root.querySelector( '[data-b3rg-kb-browse-wrap]' );
  const panes = root.querySelectorAll( '[data-b3rg-kb-filter-pane]' );
  const hasFilters = Boolean( buttons.length && browseWrap && panes.length );

  const mainPanels = root.querySelector( '[data-b3rg-kb-main-panels]' );
  const searchForm = root.querySelector( '[data-b3rg-kb-search-form]' );
  const searchInput = root.querySelector( '#b3rg-kb-search-field' );
  const searchPanel = root.querySelector( '[data-b3rg-kb-search-panel]' );
  const searchLive = root.querySelector( '[data-b3rg-kb-search-live]' );
  const searchResults = root.querySelector( '[data-b3rg-kb-search-results]' );
  const restUrl = root.getAttribute( 'data-b3rg-kb-search-rest' ) || '';

  let strings = { };
  try {
    strings = JSON.parse( root.getAttribute( 'data-b3rg-kb-strings' ) || '{}' );
  } catch ( e ) {
    strings = { };
  }

  const getActiveTermId = ( ) => {
    const active = root.querySelector( '.b3rg-kb-archive__filter-btn.is-active' );
    return active ? ( active.getAttribute( 'data-b3rg-kb-term' ) || 'all' ) : 'all';
  };

  const setActive = ( activeBtn ) => {
    buttons.forEach( ( btn ) => {
      const isActive = btn === activeBtn;
      btn.classList.toggle( 'is-active', isActive );
      btn.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
    } );
  };

  const setBrowseVisible = ( visible ) => {
    browseWrap.hidden = !visible;
    browseWrap.setAttribute( 'aria-hidden', visible ? 'false' : 'true' );
  };

  const applyFilter = ( termId ) => {
    if ( !hasFilters ) {
      return;
    }
    if ( termId === 'all' ) {
      setBrowseVisible( true );
      panes.forEach( ( pane ) => {
        pane.hidden = true;
        pane.setAttribute( 'aria-hidden', 'true' );
      } );
      return;
    }
    setBrowseVisible( false );
    panes.forEach( ( pane ) => {
      const id = pane.getAttribute( 'data-b3rg-kb-filter-pane' );
      const show = id === termId;
      pane.hidden = !show;
      pane.setAttribute( 'aria-hidden', show ? 'false' : 'true' );
    } );
  };

  const resetSearchUi = ( ) => {
    if ( searchResults ) {
      searchResults.innerHTML = '';
    }
    if ( searchLive ) {
      searchLive.textContent = '';
    }
    if ( searchPanel ) {
      searchPanel.hidden = true;
      searchPanel.setAttribute( 'aria-hidden', 'true' );
    }
    if ( mainPanels ) {
      mainPanels.hidden = false;
      mainPanels.setAttribute( 'aria-hidden', 'false' );
    }
  };

  const enterSearchMode = ( ) => {
    if ( !mainPanels || !searchPanel ) {
      return;
    }
    mainPanels.hidden = true;
    mainPanels.setAttribute( 'aria-hidden', 'true' );
    searchPanel.hidden = false;
    searchPanel.setAttribute( 'aria-hidden', 'false' );
  };

  const exitSearchToBrowse = ( ) => {
    resetSearchUi( );
    if ( hasFilters ) {
      applyFilter( getActiveTermId( ) );
    }
  };

  if ( hasFilters ) {
    buttons.forEach( ( btn ) => {
      btn.addEventListener( 'click', ( ) => {
        const termId = btn.getAttribute( 'data-b3rg-kb-term' ) || 'all';
        setActive( btn );
        applyFilter( termId );
        const q = searchInput ? searchInput.value.trim( ) : '';
        if ( q !== '' && restUrl && searchResults && searchLive ) {
          enterSearchMode( );
          fetchSearchResults( q );
          return;
        }
        resetSearchUi( );
      } );
    } );

    const initial = root.querySelector( '.b3rg-kb-archive__filter-btn.is-active' );
    if ( initial ) {
      applyFilter( initial.getAttribute( 'data-b3rg-kb-term' ) || 'all' );
    }
  }

  let searchAbort = null;
  let debounceTimer = null;
  const DEBOUNCE_MS = 320;

  const setNoResultsMessage = ( ) => {
    if ( !searchResults || !strings.noResults ) {
      return;
    }
    const p = document.createElement( 'p' );
    p.className = 'b3rg-kb-archive__empty';
    p.textContent = strings.noResults;
    searchResults.replaceChildren( p );
  };

  const fetchSearchResults = async ( query ) => {
    if ( !restUrl || !searchResults || !searchLive ) {
      return;
    }
    if ( searchAbort ) {
      searchAbort.abort( );
    }
    searchAbort = new AbortController( );
    searchLive.textContent = strings.searching || '';

    try {
      const termId = getActiveTermId( );
      let url = `${ restUrl }?s=${ encodeURIComponent( query ) }`;
      if ( termId !== 'all' && /^\d+$/.test( termId ) ) {
        url += `&term=${ encodeURIComponent( termId ) }`;
      }
      const res = await fetch( url, {
        signal: searchAbort.signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      } );
      if ( !res.ok ) {
        throw new Error( 'kb search failed' );
      }
      const data = await res.json( );
      const found = typeof data.found === 'number' ? data.found : 0;

      if ( found === 0 ) {
        searchLive.textContent = '';
        setNoResultsMessage( );
        return;
      }

      if ( found === 1 ) {
        searchLive.textContent = strings.oneResult || '';
      } else if ( strings.manyResults ) {
        searchLive.textContent = strings.manyResults.replace( '%d', String( found ) );
      } else {
        searchLive.textContent = '';
      }

      searchResults.innerHTML = typeof data.html === 'string' ? data.html : '';
    } catch ( err ) {
      if ( err && err.name === 'AbortError' ) {
        return;
      }
      searchLive.textContent = strings.loadError || '';
      searchResults.innerHTML = '';
    }
  };

  const scheduleSearch = ( raw, immediate ) => {
    const q = raw.trim( );
    clearTimeout( debounceTimer );

    if ( q === '' ) {
      exitSearchToBrowse( );
      return;
    }

    enterSearchMode( );

    const run = ( ) => {
      fetchSearchResults( q );
    };

    if ( immediate ) {
      run( );
    } else {
      debounceTimer = setTimeout( run, DEBOUNCE_MS );
    }
  };

  if ( searchForm && searchInput && mainPanels && searchPanel && searchResults && searchLive && restUrl ) {
    searchForm.addEventListener( 'submit', ( ev ) => {
      ev.preventDefault( );
      scheduleSearch( searchInput.value, true );
    } );

    searchInput.addEventListener( 'input', ( ) => {
      scheduleSearch( searchInput.value, false );
    } );

    if ( searchInput.value.trim( ) !== '' ) {
      scheduleSearch( searchInput.value, true );
    }
  }

  initKbSingleTocScrollSpy( root );
} );

/**
 * Sticky TOC: highlight in-nav link for the h2 most aligned with the viewport.
 */
function initKbSingleTocScrollSpy( root ) {
  if ( !root.classList.contains( 'b3rg-knowledge-base-single' ) ) {
    return;
  }

  const toc = root.querySelector( '[data-b3rg-kb-toc]' );
  const content = root.querySelector( '.b3rg-kb-single__content' );
  if ( !toc || !content ) {
    return;
  }

  const headings = content.querySelectorAll( 'h2[id]' );
  const links = toc.querySelectorAll( 'a.b3rg-kb-single__toc-link[href^="#"]' );
  if ( !headings.length || !links.length ) {
    return;
  }

  const linkById = new Map( );
  links.forEach( ( a ) => {
    const href = a.getAttribute( 'href' ) || '';
    const id = decodeURIComponent( href.slice( 1 ) );
    if ( id ) {
      linkById.set( id, a );
    }
  } );

  const clearActive = ( ) => {
    links.forEach( ( a ) => {
      a.classList.remove( 'is-active' );
      a.removeAttribute( 'aria-current' );
    } );
  };

  const activate = ( id ) => {
    const link = linkById.get( id );
    if ( !link ) {
      return;
    }
    clearActive( );
    link.classList.add( 'is-active' );
    link.setAttribute( 'aria-current', 'location' );
  };

  let visible = new Map( );
  const observer = new IntersectionObserver(
    ( entries ) => {
      entries.forEach( ( entry ) => {
        const id = entry.target.id;
        if ( !id ) {
          return;
        }
        if ( entry.isIntersecting ) {
          visible.set( id, entry.intersectionRatio );
        } else {
          visible.delete( id );
        }
      } );

      if ( visible.size === 0 ) {
        return;
      }

      let bestId = null;
      let bestTop = Infinity;
      headings.forEach( ( h ) => {
        const id = h.id;
        if ( !id || !visible.has( id ) ) {
          return;
        }
        const top = h.getBoundingClientRect( ).top;
        if ( top >= 0 && top < bestTop ) {
          bestTop = top;
          bestId = id;
        }
      } );

      if ( !bestId ) {
        const first = Array.from( visible.keys( ) )[ 0 ];
        bestId = first;
      }

      if ( bestId ) {
        activate( bestId );
      }
    },
    {
      root: null,
      rootMargin: '-20% 0px -55% 0px',
      threshold: [ 0, 0.1, 0.25, 0.5, 0.75, 1 ],
    }
  );

  headings.forEach( ( h ) => observer.observe( h ) );

  const firstId = headings[ 0 ].id;
  if ( firstId && linkById.has( firstId ) ) {
    activate( firstId );
  }
}
